import { PromotionKind, promotionCodeSchema, toMinorUnits } from '@lombok/shared';
import type { CreatePromotionInput } from '@lombok/shared';
import { ApiError } from '@lombok/api-client';
import { parseDecimal } from '@/lib/decimal';

/** What the create/edit promotion forms hold (everything as typed; the payload is built on submit). */
export interface PromotionFormValues {
  name: string;
  description: string;
  kind: PromotionKind;
  value: string;
  startsAt: string;
  endsAt: string;
  active: boolean;
  code: string;
}

export type PromotionField = 'name' | 'value' | 'startsAt' | 'endsAt' | 'code';
export type PromotionFormErrors = Partial<Record<PromotionField, string>>;

export const EMPTY_PROMOTION_FORM: PromotionFormValues = {
  name: '',
  description: '',
  kind: PromotionKind.PERCENTAGE,
  value: '',
  startsAt: '',
  endsAt: '',
  active: true,
  code: '',
};

/** Rule shown under the value field, per promotion kind (also used as its hint). */
export function promotionValueRule(kind: PromotionKind): string {
  return kind === PromotionKind.FIXED
    ? 'El monto debe ser un número mayor o igual a 0.'
    : 'El porcentaje debe estar entre 0 y 100.';
}

/**
 * Validates the form per field and, when it is valid, builds the API payload. `requireFutureEnd`
 * is the create rule (a promotion that is born expired is rejected); editing may keep an old end.
 */
export function validatePromotionForm(
  form: PromotionFormValues,
  { requireFutureEnd, now = Date.now() }: { requireFutureEnd: boolean; now?: number },
): { errors: PromotionFormErrors; payload?: CreatePromotionInput } {
  const errors: PromotionFormErrors = {};
  const isFixed = form.kind === PromotionKind.FIXED;

  if (form.name.trim().length < 2) errors.name = 'Ingresa un nombre de al menos 2 caracteres.';

  const num = parseDecimal(form.value);
  if (form.value.trim() === '') {
    errors.value = isFixed ? 'Ingresa el monto.' : 'Ingresa el porcentaje.';
  } else if (!Number.isFinite(num)) {
    errors.value = 'Ingresa un número válido. Puedes usar coma decimal (por ejemplo 15,5).';
  } else if (num < 0 || (!isFixed && num > 100)) {
    errors.value = promotionValueRule(form.kind);
  }

  const startsMs = form.startsAt ? new Date(form.startsAt).getTime() : Number.NaN;
  const endsMs = form.endsAt ? new Date(form.endsAt).getTime() : Number.NaN;
  if (!form.startsAt || Number.isNaN(startsMs)) errors.startsAt = 'Indica la fecha de inicio.';
  if (!form.endsAt || Number.isNaN(endsMs)) {
    errors.endsAt = 'Indica la fecha de fin.';
  } else if (!errors.startsAt && endsMs <= startsMs) {
    errors.endsAt = 'La fecha de fin debe ser posterior al inicio.';
  } else if (requireFutureEnd && endsMs <= now) {
    errors.endsAt = 'La fecha de fin ya pasó: la promoción nacería vencida.';
  }

  const code = form.code.trim();
  if (code) {
    const parsed = promotionCodeSchema.safeParse(code);
    if (!parsed.success) errors.code = parsed.error.issues[0]?.message ?? 'El código no es válido.';
  }

  if (Object.keys(errors).length > 0) return { errors };

  return {
    errors,
    payload: {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      kind: form.kind,
      value: isFixed ? toMinorUnits(num, 'USD') : num,
      startsAt: new Date(startsMs).toISOString(),
      endsAt: new Date(endsMs).toISOString(),
      active: form.active,
      code: code || undefined,
    },
  };
}

/**
 * Places a server rejection next to the field it is about (duplicate code, end before start);
 * anything else stays a general message for the banner.
 */
export function promotionServerError(err: unknown, fallback: string): {
  field?: PromotionField;
  message: string;
} {
  const message = err instanceof ApiError ? err.message : fallback;
  const lower = message.toLowerCase();
  if (lower.includes('código')) return { field: 'code', message };
  if (lower.includes('fecha de fin')) return { field: 'endsAt', message };
  return { message };
}

/**
 * The errors left after the user edits `key`: that field's own message goes away, and so does
 * the value's when the kind changes (its rule is "0-100" or "0 or more" depending on the kind).
 */
export function clearFieldError(
  errors: PromotionFormErrors,
  key: keyof PromotionFormValues,
): PromotionFormErrors {
  const next = { ...errors };
  delete next[key as PromotionField];
  if (key === 'kind') delete next.value;
  return next;
}
